import {
    all,
    call,
    cancel,
    fork,
    put,
    race,
    select,
    take,
    takeEvery,
    takeLatest
} from 'redux-saga/effects';
import axios from 'axios';
import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import { stopSubmit } from 'redux-form';

// Types
import type { Saga } from 'redux-saga';

// Constants
import {
    DOCUMENT_SETS_LOAD_REQUESTED,
    DOCUMENT_SETS_LOAD_CANCELED,
    DOCUMENT_SET_LOAD_REQUESTED,
    DOCUMENT_SET_DELETE_REQUESTED
} from './constants';

// Actions
import { enqueueSnackbar } from '../notifications/actions';
import { setResult } from '../results/actions';
import { setCount } from '../counts/actions';
import { setEvents, mergeEvents } from '../events/actions';
import { mergeDocuments } from '../documents/actions';
import { updateQueryString } from '../router/actions';
import {
    succeededLoadDocumentSets,
    failedLoadDocumentSets,
    succeededLoadDocumentSet,
    failedLoadDocumentSet,
    mergeDocumentSets,
    documentSetDeleted,
    failedDeleteDocumentSet
} from './actions';

// Selectors
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
import { idTokenSelector } from '../auth/selectors';
// HACK: disobeys folder structure rules
import { makeSelectParameters } from '../../scenes/Jobs/components/DocumentSetsRequest/selectors';

// Utils
import axiosPoll from '../../utils/axiosPoll';

// Environment
const { REACT_APP_API_URL } = process.env;

type Action = {
    +type: string,
    +payload: Object
};

export function* loadDocumentSets(): Saga<void> {
    // Select params
    const parameters = yield select(makeSelectParameters());

    // Select Current Organization Id
    const organizationId = yield select(selectCurrentOrganizationId);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/DocumentSets`,
            params: {
                count: true,
                organizationId,
                ...parameters,
                filter: {
                    ...parameters.filter,
                    eager: '[assignedUsers, documentSetAttributes, event]'
                }
            }
        },
        succeeded: loadDocumentSetsSucceeded,
        failed: loadDocumentSetsFailed
    });
}

export function* loadDocumentSetsSucceeded(response: Object): Saga<void> {
    const count = get(response, 'headers.x-total-count');

    // Merge Document Sets
    yield put(mergeDocumentSets(response.data));

    const events = response.data.map(({ event }) => event);

    // Set Events
    yield put(setEvents(events));

    yield put(setCount('documentSets', count));
    yield put(
        setResult(
            'documentSets',
            response.data.map((value) => value.id)
        )
    );

    // Document Sets Loaded
    yield put(succeededLoadDocumentSets(response));
}

export function* loadDocumentSetsFailed(error: Error): Saga<void> {
    yield put(failedLoadDocumentSets(error));
}

export function* watchDocumentSetsLoadRequested(): Saga<void> {
    let lastTask;
    while (true) {
        const [requested] = yield race([
            take(DOCUMENT_SETS_LOAD_REQUESTED),
            take(DOCUMENT_SETS_LOAD_CANCELED)
        ]);
        if (lastTask) {
            yield cancel(lastTask);
        }
        if (requested) {
            lastTask = yield fork(loadDocumentSets);
        }
    }
}

// load general jobs
export function* loadDocumentSet(action): Saga<void> {
    const { documentSetId } = action.payload;
    // Select Current Organization Id
    const [idToken, organizationId] = yield all([
        select(idTokenSelector),
        select(selectCurrentOrganizationId)
    ]);

    try {
        const response = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/DocumentSets`,
            params: {
                organizationId,
                filter: {
                    where: {
                        'documents.deletedAt': { $exists: false },
                        'documents.documentAttributes.deletedAt': {
                            $exists: false
                        },
                        'documentSetAttributes.isVisible': true
                    },
                    require: {
                        'event.entity.archivedAt': { $exists: false },
                        'event.entity.deletedAt': { $exists: false },
                        deletedAt: { $exists: false },
                        id: documentSetId
                    },
                    eager: '[assignedUsers, documentSetAttributes, event, documents.documentAttributes]'
                }
            },
            headers: {
                Authorization: idToken
            }
        });

        if (!response.data.length) {
            throw new Error('Data not found');
        }

        yield call(loadDocumentSetSucceeded, response);
    } catch (err) {
        console.log(err);
        yield call(loadDocumentSetFailed, err);
    }
}

export function* loadDocumentSetSucceeded(response: Object): Saga<void> {
    // Merge Document Set
    yield put(mergeDocumentSets(response.data));

    const events = response.data.map(({ event }) => event);

    // Merge Event
    yield put(mergeEvents(events));

    const { documents } = response.data[0];

    if (documents && documents.length) {
        yield put(mergeDocuments(keyBy(documents, 'uuid')));
    }

    // Document Set Loaded
    yield put(succeededLoadDocumentSet(response));
}

export function* loadDocumentSetFailed(error: Error): Saga<void> {
    yield put(failedLoadDocumentSet(error));
}

export function* watchDocumentSetLoadRequested(): Saga<void> {
    yield takeLatest(DOCUMENT_SET_LOAD_REQUESTED, loadDocumentSet);
}

export function* deleteDocumentSet(action: Action): Saga<void> {
    const { id } = action.payload;

    const idToken = yield select(idTokenSelector);

    const config = {
        method: 'delete',
        url: `${REACT_APP_API_URL}/v1/DocumentSets/${id}`,
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response = yield call(axios, config);

        // Select params
        const parameters = yield select(makeSelectParameters());

        // Select Current Organization Id
        const organizationId = yield select(selectCurrentOrganizationId);

        const getResponse = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/DocumentSets`,
            headers: {
                Authorization: idToken
            },
            params: {
                count: true,
                organizationId,
                ...parameters,
                filter: {
                    ...parameters.filter,
                    eager: '[assignedUsers, documentSetAttributes, event]'
                }
            }
        });

        yield call(loadDocumentSetsSucceeded, getResponse);

        // Document Set Deleted
        yield put(documentSetDeleted(response));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Job deleted successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                deleteJobModal: undefined,
                selectedJobId: undefined
            })
        );
    } catch (error) {
        // Failed Delete Document Set
        yield put(failedDeleteDocumentSet(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('DOCUMENT_SET_DELETE', { _error: message }));
    }
}

export function* watchDeleteDocumentSet(): Saga<void> {
    yield takeEvery(DOCUMENT_SET_DELETE_REQUESTED, deleteDocumentSet);
}

export default function* documentSetsSagas(): Saga<void> {
    yield all([
        fork(watchDocumentSetsLoadRequested),
        fork(watchDocumentSetLoadRequested),
        fork(watchDeleteDocumentSet)
    ]);
}
